import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STATUS = {
  pending: { label: 'New', color: 'warning' },
  proposed: { label: 'Time offered', color: 'info' },
  confirmed: { label: 'Confirmed', color: 'success' },
  declined: { label: 'Declined', color: 'default' },
  cancelled: { label: 'Cancelled by patient', color: 'default' },
};

const ACTIONS = {
  confirm: { title: 'Confirm the appointment', button: 'Confirm', color: 'primary' },
  propose: { title: 'Offer a different time', button: 'Send offer', color: 'primary' },
  decline: { title: 'Decline the request', button: 'Decline', color: 'error' },
};

const fmtDate = (iso) => (iso ? new Date(`${iso}T00:00:00`)
  .toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) : '');
const when = (d, t) => (d ? `${fmtDate(d)}${t ? ` at ${t}` : ''}` : '');

/**
 * Patients ask for visits from their portal. The front desk answers here: confirm it,
 * offer another time (the patient accepts or declines it in their portal), or decline.
 * The patient is told either way.
 */
export default function AppointmentRequests() {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState(null);
  const [answering, setAnswering] = useState(null);   // { row, action }
  const [form, setForm] = useState({ date: '', time: '', note: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/v1/admin/appointment-requests`, {
        params: { status: tab === 'pending' ? 'pending' : 'all' },
      });
      const list = data.appointments || [];
      // "Open" means anything still waiting on someone — new, or a time we offered.
      setRows(tab === 'pending' ? list
        : tab === 'open' ? list.filter((r) => r.status === 'proposed') : list);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load appointment requests');
      setRows([]);
    }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const open = (row, action) => {
    setAnswering({ row, action });
    setForm({ date: row.preferred_date || '', time: row.preferred_time || '', note: '' });
  };

  const submit = async () => {
    const { row, action } = answering;
    if (action === 'propose' && !form.date) { toast.error('Choose the date you are offering'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/v1/admin/appointment-requests/${row.id}`, {
        action,
        staff_note: form.note.trim() || null,
        scheduled_date: action === 'decline' ? null : (form.date || null),
        scheduled_time: action === 'decline' ? null : (form.time.trim() || null),
      });
      toast.success(action === 'confirm' ? 'Confirmed — the patient has been told'
        : action === 'propose' ? 'Offer sent to the patient' : 'Request declined');
      setAnswering(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg : (d || 'Could not answer that request'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700}>Appointment requests</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Visits patients have asked for from their portal. They are told whatever you decide.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="pending" label="New" />
        <Tab value="open" label="Waiting on the patient" />
        <Tab value="all" label="All" />
      </Tabs>

      {rows === null ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !rows.length ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Nothing here.</Typography>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((r) => {
            const st = STATUS[r.status] || { label: r.status, color: 'default' };
            const actionable = r.status === 'pending' || r.status === 'proposed';
            return (
              <Card key={r.id} variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={700}>
                        {r.patient_name || r.patient_email}
                        {r.provider_name ? ` · for ${r.provider_name}` : ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{r.reason}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {r.preferred_date ? `Asked for ${when(r.preferred_date, r.preferred_time)}` : 'No preferred time'}
                        {r.patient_phone ? ` · ${r.patient_phone}` : ''}
                      </Typography>
                      {r.scheduled_date && (r.status === 'confirmed' || r.status === 'proposed') && (
                        <Typography variant="body2" fontWeight={600}>
                          {r.status === 'confirmed' ? 'Booked for ' : 'Offered '}{when(r.scheduled_date, r.scheduled_time)}
                        </Typography>
                      )}
                    </Box>
                    <Chip size="small" label={st.label} color={st.color} />
                  </Stack>
                  {actionable && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                      <Button size="small" variant="contained" onClick={() => open(r, 'confirm')}>Confirm</Button>
                      <Button size="small" variant="outlined" onClick={() => open(r, 'propose')}>Offer another time</Button>
                      <Button size="small" color="error" onClick={() => open(r, 'decline')}>Decline</Button>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Dialog open={!!answering} onClose={() => !saving && setAnswering(null)} fullWidth maxWidth="xs">
        {answering && (
          <>
            <DialogTitle>{ACTIONS[answering.action].title}</DialogTitle>
            <DialogContent>
              {answering.action !== 'decline' && (
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                  <TextField fullWidth type="date" label="Date" InputLabelProps={{ shrink: true }}
                    value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                  <TextField fullWidth label="Time" placeholder="10:30" value={form.time}
                    inputProps={{ maxLength: 50 }} onChange={(e) => setForm({ ...form, time: e.target.value })} />
                </Stack>
              )}
              <TextField fullWidth multiline minRows={2} label="Note to the patient (optional)" sx={{ mt: 2 }}
                value={form.note} inputProps={{ maxLength: 500 }}
                onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAnswering(null)} disabled={saving}>Cancel</Button>
              <Button variant="contained" color={ACTIONS[answering.action].color} onClick={submit} disabled={saving}>
                {ACTIONS[answering.action].button}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
